/**
 * The one call-recording player in Orbit.
 *
 * Extracted from `PhoneCallTimelineItem` so the CI manager dashboard's call log
 * plays recordings the same way the patient timeline does, rather than growing a
 * second player with its own idea of playback speed and its own handling of
 * `Binary/` attachments. The phone log's play-recording modal is the third such
 * caller. Every surface that plays a recording renders this; nothing else should.
 *
 * Multi-segment playback lives here too: a long call arrives as several Twilio
 * recordings, and advancing to the next one when the current ends is what makes
 * them read as a single call.
 */

import { getAttachmentBlob } from '@/services/attachments';
import { Button, Group, Text } from '@mantine/core';
import { Attachment } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { ReactElement, useEffect, useRef, useState } from 'react';
import AudioPlayer from 'react-h5-audio-player';

const SPEED_OPTIONS = [
  { label: '0.75x', value: 0.75 },
  { label: '1x', value: 1 },
  { label: '1.25x', value: 1.25 },
  { label: '1.5x', value: 1.5 },
  { label: '2x', value: 2 },
  { label: '3x', value: 3 },
  { label: '4x', value: 4 },
];

function SpeedControl({
  playbackRate,
  onSpeedChange,
}: {
  playbackRate: number;
  onSpeedChange: (speed: number) => void;
}): ReactElement {
  return (
    <Group gap={4} style={{ marginLeft: '8px' }}>
      {SPEED_OPTIONS.map((option) => (
        <Button
          key={option.value}
          size="xs"
          variant={playbackRate === option.value ? 'filled' : 'subtle'}
          color={playbackRate === option.value ? 'blue' : 'gray'}
          onClick={() => onSpeedChange(option.value)}
          style={{ fontSize: '10px', height: '24px', minWidth: '32px', padding: '0 6px' }}
        >
          {option.label}
        </Button>
      ))}
    </Group>
  );
}

/**
 * A playable URL for one attachment.
 *
 * A Twilio recording is already an absolute URL and plays directly. A `Binary/`
 * attachment has to be fetched through the Medplum client and handed to the
 * player as an object URL, which is then revoked on unmount — without that the
 * blob leaks for the lifetime of the page.
 */
export function useRecordingPlaybackUrl(attachment: Attachment | undefined): string | undefined {
  const medplum = useMedplum();
  const [playbackUrl, setPlaybackUrl] = useState<string | undefined>();

  useEffect(() => {
    let isCancelled = false;
    let objectUrl: string | undefined;

    const resolvePlaybackUrl = async (): Promise<void> => {
      if (!attachment?.url) {
        setPlaybackUrl(undefined);
        return;
      }

      if (attachment.url.startsWith('Binary/')) {
        try {
          const fileInfo = await getAttachmentBlob(medplum, attachment);
          if (!isCancelled && fileInfo?.url) {
            objectUrl = fileInfo.url;
            setPlaybackUrl(fileInfo.url);
          }
        } catch (error) {
          if (!isCancelled) {
            setPlaybackUrl(undefined);
          }
          // eslint-disable-next-line no-console
          console.error(
            `Error resolving call recording blob: ${error instanceof Error ? error.message : 'Unknown error'}`,
            error
          );
        }
        return;
      }

      setPlaybackUrl(attachment.url);
    };

    void resolvePlaybackUrl();

    return () => {
      isCancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [medplum, attachment, attachment?.url]);

  return playbackUrl;
}

/** What a play/pause listener needs; the player owns both, so it reports them. */
export interface CallRecordingPlaybackEvent {
  playbackRate: number;
  currentTimeSeconds: number | undefined;
}

export interface CallRecordingPlayerProps {
  /** Every recording segment for the call, in the order Twilio produced them. */
  attachments: Attachment[];
  /**
   * Resets segment playback when the surrounding row changes identity. Without
   * it a virtualised or filtered list can leave segment 3 of the previous call
   * selected for the next one.
   */
  resetKey?: string;
  onPlay?: (event: CallRecordingPlaybackEvent) => void;
  onPause?: (event: CallRecordingPlaybackEvent) => void;
  /** Wraps the rendered player; the timeline uses it for the highlight anchor. */
  children?: (player: ReactElement) => ReactElement;
  style?: React.CSSProperties;
}

const DEFAULT_PLAYER_STYLE: React.CSSProperties = {
  fontSize: '14px',
  backgroundColor: '#f8f9fa',
  boxShadow: 'none',
  border: '1px solid #e9ecef',
  borderRadius: '8px',
};

export function CallRecordingPlayer({
  attachments,
  resetKey,
  onPlay,
  onPause,
  children,
  style,
}: CallRecordingPlayerProps): ReactElement | null {
  const audioPlayerRef = useRef<any>(null);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [segmentIndex, setSegmentIndex] = useState(0);

  const attachmentUrlsKey = attachments.map((attachment) => attachment.url).join('|');
  const attachment = attachments[segmentIndex];
  const playbackUrl = useRecordingPlaybackUrl(attachment);

  useEffect(() => {
    setSegmentIndex(0);
  }, [resetKey, attachmentUrlsKey]);

  // Auto-continue: only from segment 2 onward, so opening a row never starts
  // playing on its own.
  useEffect(() => {
    if (segmentIndex > 0) {
      void audioPlayerRef.current?.audio?.current?.play();
    }
  }, [segmentIndex, playbackUrl]);

  const handleSpeedChange = (speed: number): void => {
    setPlaybackRate(speed);
    if (audioPlayerRef.current?.audio?.current) {
      audioPlayerRef.current.audio.current.playbackRate = speed;
    }
  };

  if (!attachment || !playbackUrl) {
    return null;
  }

  const player = (
    <>
      {attachments.length > 1 && (
        <Text size="xs" c="dimmed" mb={4}>
          Recording part {segmentIndex + 1} of {attachments.length}
        </Text>
      )}
      <AudioPlayer
        key={playbackUrl}
        ref={audioPlayerRef}
        src={playbackUrl}
        showJumpControls={false}
        showSkipControls={false}
        showFilledProgress={false}
        onPlay={() =>
          onPlay?.({ playbackRate, currentTimeSeconds: audioPlayerRef.current?.audio?.current?.currentTime })
        }
        onPause={() =>
          onPause?.({ playbackRate, currentTimeSeconds: audioPlayerRef.current?.audio?.current?.currentTime })
        }
        onEnded={() => {
          if (segmentIndex < attachments.length - 1) {
            setSegmentIndex((index) => index + 1);
          }
        }}
        customAdditionalControls={[
          <SpeedControl key="speed-control" playbackRate={playbackRate} onSpeedChange={handleSpeedChange} />,
        ]}
        style={{ ...DEFAULT_PLAYER_STYLE, ...style }}
      />
    </>
  );

  return children ? children(player) : player;
}
